import { Link } from "react-router-dom";
import { ArrowRight } from "lucide-react";
import SEO from "@/components/SEO";
import ShareRow from "@/components/ShareRow";
import { Reveal, MaskLine, SectionHead } from "@/components/Reveal";
import { ARTICLES_74, MISSIONS } from "@/data/content";

const Mandate = () => (
  <>
    <SEO
      title="The Constitutional Mandate — 74th Amendment & Urban Governance"
      path="/mandate"
      description="How Smart Nagar Nigam operationalises the 74th Constitutional Amendment — Articles 243-Q to 243-ZE, the 12th Schedule — and closes the last-mile gap of Smart Cities Mission, AMRUT, PMAY, Swachh Bharat, Digital India, MPD-2047 and Viksit Bharat 2047."
    />
    <section className="relative overflow-hidden pt-28 md:pt-52" data-testid="mandate-hero">
      <div className="mx-auto max-w-[1600px] px-5 pb-20 md:px-10 md:pb-28">
        <MaskLine delay={0.1}>
          <span className="text-xs uppercase tracking-[0.2em] sm:tracking-[0.35em] text-gold md:text-sm">The Constitutional Mandate</span>
        </MaskLine>
        <h1 className="mt-6 font-display font-extrabold uppercase leading-[0.95] tracking-tight text-ivory">
          <MaskLine delay={0.25} className="text-[11vw] md:text-[7vw]">The 74th</MaskLine>
          <MaskLine delay={0.4} className="text-[11vw] md:text-[7vw]">
            <span className="text-outline">Amendment, Made Real.</span>
          </MaskLine>
        </h1>
        <MaskLine delay={0.55}>
          <p className="mt-8 max-w-3xl text-base leading-relaxed text-white/70 md:text-lg">
            Enacted by Parliament in December 1992 and brought into force on 1st June 1993,
            the 74th Constitutional Amendment is the foundational charter of urban democratic
            governance in India. Smart Nagar Nigam's entire architecture is a direct response
            to its unfinished promise.
          </p>
        </MaskLine>
      </div>
    </section>

    <section className="border-t border-white/10" data-testid="mandate-articles">
      <div className="mx-auto max-w-[1600px] px-5 py-20 md:px-10 md:py-32">
        <SectionHead index="01" overline="Part IX-A · Articles 243-P to 243-ZG" title="The Promise, and How We Operationalise It." />
        <p className="mb-12 max-w-3xl text-base leading-relaxed text-white/70 md:text-lg">
          By inserting Part IX-A and the 12th Schedule into the Constitution, the Amendment
          made municipalities no longer creatures of state policy but institutions of
          constitutional standing.
        </p>
        <div className="border border-white/10">
          {ARTICLES_74.map((a, i) => (
            <Reveal key={a.article} delay={i * 0.03}>
              <div className="grid gap-6 border-b border-white/10 p-8 last:border-b-0 md:grid-cols-12 md:p-10">
                <div className="md:col-span-3">
                  <p className="font-display text-2xl font-extrabold tracking-tight text-gold">Article {a.article}</p>
                  <p className="mt-2 text-xs uppercase tracking-[0.2em] text-white/50">{a.provision}</p>
                </div>
                <div className="md:col-span-4">
                  <p className="text-[11px] uppercase tracking-[0.25em] text-white/40">The Promise</p>
                  <p className="mt-2 text-sm leading-relaxed text-white/70">{a.promise}</p>
                </div>
                <div className="md:col-span-5">
                  <p className="text-[11px] uppercase tracking-[0.25em] text-gold">Smart Nagar Nigam's Contribution</p>
                  <p className="mt-2 text-sm leading-relaxed text-white/70">{a.snn}</p>
                </div>
              </div>
            </Reveal>
          ))}
        </div>
        <Reveal delay={0.1}>
          <p className="mt-10 max-w-4xl text-sm leading-relaxed text-white/60">
            The 12th Schedule lists 18 functional domains assigned to municipalities — from
            urban planning and land use to public health, education, fire services, urban
            forestry, environmental protection and slum improvement. All 30 MCD domains mapped
            by Smart Nagar Nigam fall within or directly adjacent to this mandate. Smart Nagar
            Nigam is, in the deepest constitutional sense, a platform for making Article
            243-W's promise real.
          </p>
          <p className="mt-8 max-w-3xl border-l-2 border-gold pl-6 font-display text-xl font-bold leading-snug text-ivory md:text-2xl">
            "In seventy-five years of Independence, the 74th Amendment's promise of ward-level
            democratic accountability has never been operationalised at scale. Smart Nagar
            Nigam exists to complete that unfinished project."
          </p>
        </Reveal>
      </div>
    </section>

    <section className="section-light border-t border-white/10" data-testid="mandate-missions">
      <div className="mx-auto max-w-[1600px] px-5 py-20 md:px-10 md:py-32">
        <SectionHead index="02" overline="National Goals, Global Frameworks" title="The Missing Last-Mile Layer of India's Urban Missions." />
        <p className="mb-12 max-w-3xl text-base leading-relaxed text-white/70 md:text-lg">
          Smart Nagar Nigam's programme is not parallel to India's national urban missions —
          it is the missing last-mile implementation layer that makes those missions
          meaningful at the ward level.
        </p>
        <div className="grid gap-px border border-white/10 bg-white/10 md:grid-cols-2">
          {MISSIONS.map((m, i) => (
            <Reveal key={m.name} delay={i * 0.04} className="bg-coal p-8 md:p-10">
              <h3 className="font-display text-xl font-bold leading-snug text-ivory md:text-2xl">{m.name}</h3>
              <p className="mt-2 text-xs uppercase tracking-[0.2em] text-gold">{m.scale}</p>
              <p className="mt-4 text-sm leading-relaxed text-white/60">{m.gap}</p>
            </Reveal>
          ))}
        </div>
        <Reveal delay={0.1}>
          <div className="mt-12 flex flex-wrap items-center gap-8">
            <Link to="/initiatives" data-testid="mandate-initiatives-cta" className="inline-flex items-center gap-3 bg-gold px-8 py-4 font-display text-sm font-bold uppercase tracking-[0.15em] text-obsidian transition-colors duration-300 hover:bg-gold-light">
              See the Initiatives <ArrowRight size={16} />
            </Link>
            <ShareRow title="The Constitutional Mandate — 74th Amendment & Urban Governance" path="/mandate" />
          </div>
        </Reveal>
      </div>
    </section>
  </>
);

export default Mandate;
